import { countries, COUNTRY_CODES } from "@redotech/locale/countries";
import { USA_PROVINCE_CODES, usaProvinces } from "@redotech/locale/provinces";
import { z } from "zod";
import { zExt } from "../common/zod-util";

export enum CheckoutTreeNodeType {
  SPLIT_TEST = "SPLIT_TEST",
  CHECKOUT_EXPERIENCE = "CHECKOUT_EXPERIENCE",
  SEGMENT = "SEGMENT",
}

// ───────────────────────────────────────────────────────────────────────────────
//   The recursive union of all three node-types
// ───────────────────────────────────────────────────────────────────────────────
export const CheckoutTreeNodeSchema = z.lazy(() =>
  z.union([
    CheckoutTreeCheckoutExperienceNodeSchema,
    CheckoutTreeSplitTestNodeSchema,
    CheckoutTreeSegmentNodeSchema,
  ]),
);
export type CheckoutTreeNode = z.infer<typeof CheckoutTreeNodeSchema>;

export const CheckoutTreeNodeOptionalIdSchema = z.lazy(() =>
  z.union([
    CheckoutTreeCheckoutExperienceNodeOptionalIdSchema,
    CheckoutTreeSplitTestNodeOptionalIdSchema,
    CheckoutTreeSegmentNodeOptionalIdSchema,
  ]),
);
export type CheckoutTreeNodeOptionalId = z.infer<
  typeof CheckoutTreeNodeOptionalIdSchema
>;

// ───────────────────────────────────────────────────────────────────────────────
//   CheckoutExperience node (no recursion)
// ───────────────────────────────────────────────────────────────────────────────
const CheckoutTreeCheckoutExperienceNodeSchema_ = z.object({
  nodeType: z.literal(CheckoutTreeNodeType.CHECKOUT_EXPERIENCE),
  checkoutExperienceId: zExt.objectId(),
  _id: zExt.objectId(),
});
const CheckoutTreeCheckoutExperienceNodeSchema: z.ZodType<CheckoutTreeCheckoutExperienceNode> =
  CheckoutTreeCheckoutExperienceNodeSchema_;
export interface CheckoutTreeCheckoutExperienceNode
  extends z.infer<typeof CheckoutTreeCheckoutExperienceNodeSchema_> {}

const CheckoutTreeCheckoutExperienceNodeOptionalIdSchema_ =
  CheckoutTreeCheckoutExperienceNodeSchema_.partial({ _id: true });
const CheckoutTreeCheckoutExperienceNodeOptionalIdSchema: z.ZodType<CheckoutTreeCheckoutExperienceNodeOptionalId> =
  CheckoutTreeCheckoutExperienceNodeOptionalIdSchema_;
export interface CheckoutTreeCheckoutExperienceNodeOptionalId
  extends z.infer<typeof CheckoutTreeCheckoutExperienceNodeOptionalIdSchema_> {}

// ───────────────────────────────────────────────────────────────────────────────
//   SplitTest edge (recursive)
// ───────────────────────────────────────────────────────────────────────────────
const CheckoutTreeSplitTestEdgeSchema_ = z.object({
  childNode: CheckoutTreeNodeSchema,
  treatmentId: zExt.objectId(),
});
const CheckoutTreeSplitTestEdgeSchema: z.ZodType<CheckoutTreeSplitTestEdge> =
  CheckoutTreeSplitTestEdgeSchema_;
export interface CheckoutTreeSplitTestEdge
  extends z.infer<typeof CheckoutTreeSplitTestEdgeSchema_> {}

const CheckoutTreeSplitTestEdgeOptionalIdSchema_ = z.object({
  childNode: CheckoutTreeNodeOptionalIdSchema,
  treatmentId: zExt.objectId(),
});
const CheckoutTreeSplitTestEdgeOptionalIdSchema: z.ZodType<CheckoutTreeSplitTestEdgeOptionalId> =
  CheckoutTreeSplitTestEdgeOptionalIdSchema_;
export interface CheckoutTreeSplitTestEdgeOptionalId
  extends z.infer<typeof CheckoutTreeSplitTestEdgeOptionalIdSchema_> {}

// ───────────────────────────────────────────────────────────────────────────────
//   SplitTest node
// ───────────────────────────────────────────────────────────────────────────────
const CheckoutTreeSplitTestNodeSchema_ = z.object({
  nodeType: z.literal(CheckoutTreeNodeType.SPLIT_TEST),
  splitTestId: zExt.objectId(),
  edges: z.array(CheckoutTreeSplitTestEdgeSchema),
  _id: zExt.objectId(),
});
const CheckoutTreeSplitTestNodeSchema: z.ZodType<CheckoutTreeSplitTestNode> =
  CheckoutTreeSplitTestNodeSchema_;
export interface CheckoutTreeSplitTestNode
  extends z.infer<typeof CheckoutTreeSplitTestNodeSchema_> {}

const CheckoutTreeSplitTestNodeOptionalIdSchema_ =
  CheckoutTreeSplitTestNodeSchema_.partial({ _id: true })
    .omit({ edges: true })
    .extend({ edges: z.array(CheckoutTreeSplitTestEdgeOptionalIdSchema) });
const CheckoutTreeSplitTestNodeOptionalIdSchema: z.ZodType<CheckoutTreeSplitTestNodeOptionalId> =
  CheckoutTreeSplitTestNodeOptionalIdSchema_;
export interface CheckoutTreeSplitTestNodeOptionalId
  extends z.infer<typeof CheckoutTreeSplitTestNodeOptionalIdSchema_> {}

// ───────────────────────────────────────────────────────────────────────────────
//   Segments
// ───────────────────────────────────────────────────────────────────────────────
export enum SegmentableCheckoutCharacteristic {
  /**
   * The distance between the destination address and the fulfillment center.
   */
  DELIVERY_DISTANCE_MILES = "DELIVERY_DISTANCE_MILES",
  /**
   * Number of customer's previous orders.
   */
  ORDER_COUNT = "ORDER_COUNT",
  /**
   * The location of the delivery address.
   */
  DELIVERY_LOCATION = "DELIVERY_LOCATION",
  /**
   * Customer's tags.
   */
  CUSTOMER_TAGS = "CUSTOMER_TAGS",
  /**
   * Product tags.
   */
  PRODUCT_TAGS = "PRODUCT_TAGS",
  /**
   * Product vendor.
   */
  PRODUCT_VENDOR = "PRODUCT_VENDOR",
  /**
   * Product SKU.
   */
  PRODUCT_SKU = "PRODUCT_SKU",
  /**
   * Quantity of items in the cart (excluding products with SKU "x-redo").
   */
  ITEM_QUANTITY = "ITEM_QUANTITY",
  /**
   * The location of the origin address.
   */
  ORIGIN_LOCATION = "ORIGIN_LOCATION",
}

export enum SegmentNumberOperator {
  LESS_THAN = "LESS_THAN",
  LESS_THAN_OR_EQUAL = "LESS_THAN_OR_EQUAL",
  EQUAL = "EQUAL",
  GREATER_THAN = "GREATER_THAN",
  GREATER_THAN_OR_EQUAL = "GREATER_THAN_OR_EQUAL",
}

export enum SegmentListSingleItemMembershipOperator {
  IN = "IN",
  NOT_IN = "NOT_IN",
}

export enum SegmentListMultipleItemMembershipOperator {
  ALL_IN = "ALL_IN",
  ANY_IN = "ANY_IN",
  NONE_IN = "NONE_IN",
}

// ───────────────────────────────────────────────────────────────────────────────
//   Segment edge (recursive)
// ───────────────────────────────────────────────────────────────────────────────
const CheckoutTreeSegmentEdgeSchema_ = z.object({
  childNode: CheckoutTreeNodeSchema,
});
const CheckoutTreeSegmentEdgeSchema: z.ZodType<CheckoutTreeSegmentEdge> =
  CheckoutTreeSegmentEdgeSchema_;
export interface CheckoutTreeSegmentEdge
  extends z.infer<typeof CheckoutTreeSegmentEdgeSchema_> {}

const CheckoutTreeSegmentEdgeOptionalIdSchema_ = z.lazy(() =>
  CheckoutTreeSegmentEdgeSchema_.partial({ childNode: true }),
);
const CheckoutTreeSegmentEdgeOptionalIdSchema: z.ZodType<CheckoutTreeSegmentEdgeOptionalId> =
  CheckoutTreeSegmentEdgeOptionalIdSchema_;
export interface CheckoutTreeSegmentEdgeOptionalId
  extends z.infer<typeof CheckoutTreeSegmentEdgeOptionalIdSchema_> {}

// Delivery distance miles segment
const DeliveryDistanceMilesSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.DELIVERY_DISTANCE_MILES,
  ),
  miles: z.number(),
  operator: z.enum(SegmentNumberOperator),
});
export const DeliveryDistanceMilesSegmentSchema: z.ZodType<DeliveryDistanceMilesSegment> =
  DeliveryDistanceMilesSegmentSchema_;
export interface DeliveryDistanceMilesSegment
  extends z.infer<typeof DeliveryDistanceMilesSegmentSchema_> {}

// Order count segment
const OrderCountSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.ORDER_COUNT,
  ),
  numOrders: z.number(),
  operator: z.enum(SegmentNumberOperator),
});
export const OrderCountSegmentSchema: z.ZodType<OrderCountSegment> =
  OrderCountSegmentSchema_;
export interface OrderCountSegment
  extends z.infer<typeof OrderCountSegmentSchema_> {}

// Delivery location segment
const DeliveryLocationSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.DELIVERY_LOCATION,
  ),
  locationCountry: z.object({
    countries: z.array(z.enum(countries.map((c) => c.code))),
    operator: z.enum(SegmentListSingleItemMembershipOperator),
  }),
  // Right now, we only support USA provinces.
  locationProvince: z
    .object({
      provinces: z.array(z.enum(usaProvinces.map((p) => p.code))),
      operator: z.enum(SegmentListSingleItemMembershipOperator),
    })
    .optional(),
});
export const DeliveryLocationSegmentSchema: z.ZodType<DeliveryLocationSegment> =
  DeliveryLocationSegmentSchema_;
export interface DeliveryLocationSegment
  extends z.infer<typeof DeliveryLocationSegmentSchema_> {}

// Customer tags segment
/**
 * Due to limitations in Shopify's delivery customization function, we can only
 * support this subset of multiple item list membership operators for customer
 * tags segments.
 */
export const CustomerTagsSegmentListMultipleItemMembershipOperator = [
  SegmentListMultipleItemMembershipOperator.ANY_IN,
  SegmentListMultipleItemMembershipOperator.NONE_IN,
] as const;
export type CustomerTagsSegmentListMultipleItemMembershipOperator =
  (typeof CustomerTagsSegmentListMultipleItemMembershipOperator)[number];

/**
 * Due to limitations in Shopify's delivery customization function, we only allow
 * two customer tags segments nodes in a checkout tree for now.
 */
export enum CustomerTagsSegmentNodeIdentifier {
  CUSTOMER_TAGS_SEGMENT_NODE_ONE = "CUSTOMER_TAGS_SEGMENT_NODE_ONE",
  CUSTOMER_TAGS_SEGMENT_NODE_TWO = "CUSTOMER_TAGS_SEGMENT_NODE_TWO",
}

const CustomerTagsSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.CUSTOMER_TAGS,
  ),
  tags: z.array(z.string()),
  operator: z.enum(CustomerTagsSegmentListMultipleItemMembershipOperator),
  customerTagsSegmentNodeIdentifier: z.enum(CustomerTagsSegmentNodeIdentifier),
});
export const CustomerTagsSegmentSchema: z.ZodType<CustomerTagsSegment> =
  CustomerTagsSegmentSchema_;
export interface CustomerTagsSegment
  extends z.infer<typeof CustomerTagsSegmentSchema_> {}

// Product tags segment
/**
 * Due to limitations in Shopify's delivery customization function, we can only
 * support this subset of multiple item list membership operators for product
 * tags segments.
 */
export const ProductTagsSegmentListMultipleItemMembershipOperator = [
  SegmentListMultipleItemMembershipOperator.ANY_IN,
  SegmentListMultipleItemMembershipOperator.NONE_IN,
] as const;
export type ProductTagsSegmentListMultipleItemMembershipOperator =
  (typeof ProductTagsSegmentListMultipleItemMembershipOperator)[number];

/**
 * Due to limitations in Shopify's delivery customization function, we only allow
 * two product tags segment nodes in a checkout tree for now.
 */
export enum ProductTagsSegmentNodeIdentifier {
  PRODUCT_TAGS_SEGMENT_NODE_ONE = "PRODUCT_TAGS_SEGMENT_NODE_ONE",
  PRODUCT_TAGS_SEGMENT_NODE_TWO = "PRODUCT_TAGS_SEGMENT_NODE_TWO",
}

const ProductTagsSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.PRODUCT_TAGS,
  ),
  tags: z.array(z.string()),
  operator: z.enum(ProductTagsSegmentListMultipleItemMembershipOperator),
  cartOperator: z.enum(SegmentListMultipleItemMembershipOperator),
  productTagsSegmentNodeIdentifier: z.enum(ProductTagsSegmentNodeIdentifier),
});
export const ProductTagsSegmentSchema: z.ZodType<ProductTagsSegment> =
  ProductTagsSegmentSchema_;
export interface ProductTagsSegment
  extends z.infer<typeof ProductTagsSegmentSchema_> {}

// Product vendor segment
/**
 * Product vendors list with list membership operators for product vendor segments.
 */
const ProductVendorSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.PRODUCT_VENDOR,
  ),
  vendors: z.array(z.string()),
  operator: z.enum(SegmentListSingleItemMembershipOperator),
  cartOperator: z.enum(SegmentListMultipleItemMembershipOperator),
});
export const ProductVendorSegmentSchema: z.ZodType<ProductVendorSegment> =
  ProductVendorSegmentSchema_;
export interface ProductVendorSegment
  extends z.infer<typeof ProductVendorSegmentSchema_> {}

// Product SKU segment
/**
 * Product SKUs list with list membership operators for product SKU segments.
 */
const ProductSkuSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.PRODUCT_SKU,
  ),
  skus: z.array(z.string()),
  operator: z.enum(SegmentListSingleItemMembershipOperator),
  cartOperator: z.enum(SegmentListMultipleItemMembershipOperator),
  requireExactMatch: z.boolean(),
});
export const ProductSkuSegmentSchema: z.ZodType<ProductSkuSegment> =
  ProductSkuSegmentSchema_;
export interface ProductSkuSegment
  extends z.infer<typeof ProductSkuSegmentSchema_> {}

// Item quantity segment
const ItemQuantitySegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.ITEM_QUANTITY,
  ),
  quantity: z.number(),
  operator: z.enum(SegmentNumberOperator),
  exclusions: z.object({ skus: z.array(z.string()).optional() }).optional(),
});
export const ItemQuantitySegmentSchema: z.ZodType<ItemQuantitySegment> =
  ItemQuantitySegmentSchema_;
export interface ItemQuantitySegment
  extends z.infer<typeof ItemQuantitySegmentSchema_> {}

// Origin location segment
const OriginLocationSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentableCheckoutCharacteristic.ORIGIN_LOCATION,
  ),
  originAddress: z.object({
    address1: z.string().nullish(),
    address2: z.string().nullish(),
    city: z.string().nullish(),
    province: z.string().nullish(),
    postalCode: z.string().nullish(),
    country: z.string().nullish(),
  }),
});
export const OriginLocationSegmentSchema: z.ZodType<OriginLocationSegment> =
  OriginLocationSegmentSchema_;
export interface OriginLocationSegment
  extends z.infer<typeof OriginLocationSegmentSchema_> {}

const SegmentSchema = z.union([
  DeliveryDistanceMilesSegmentSchema,
  OrderCountSegmentSchema,
  DeliveryLocationSegmentSchema,
  CustomerTagsSegmentSchema,
  ProductTagsSegmentSchema,
  ProductVendorSegmentSchema,
  ProductSkuSegmentSchema,
  ItemQuantitySegmentSchema,
  OriginLocationSegmentSchema,
]);
export type Segment = z.infer<typeof SegmentSchema>;

// ───────────────────────────────────────────────────────────────────────────────
//   Segment node
// ───────────────────────────────────────────────────────────────────────────────
const CheckoutTreeSegmentNodeSchema_ = z.object({
  nodeType: z.literal(CheckoutTreeNodeType.SEGMENT),
  matchingEdge: CheckoutTreeSegmentEdgeSchema,
  nonMatchingEdge: CheckoutTreeSegmentEdgeSchema,
  segment: SegmentSchema,
  _id: zExt.objectId(),
});
const CheckoutTreeSegmentNodeSchema: z.ZodType<CheckoutTreeSegmentNode> =
  CheckoutTreeSegmentNodeSchema_;
export interface CheckoutTreeSegmentNode
  extends z.infer<typeof CheckoutTreeSegmentNodeSchema_> {}

const CheckoutTreeSegmentNodeOptionalIdSchema_ =
  CheckoutTreeSegmentNodeSchema_.partial({ _id: true })
    .omit({ matchingEdge: true, nonMatchingEdge: true })
    .extend({
      matchingEdge: CheckoutTreeSegmentEdgeOptionalIdSchema,
      nonMatchingEdge: CheckoutTreeSegmentEdgeOptionalIdSchema,
    });
const CheckoutTreeSegmentNodeOptionalIdSchema: z.ZodType<CheckoutTreeSegmentNodeOptionalId> =
  CheckoutTreeSegmentNodeOptionalIdSchema_;
export interface CheckoutTreeSegmentNodeOptionalId
  extends z.infer<typeof CheckoutTreeSegmentNodeOptionalIdSchema_> {}

// ───────────────────────────────────────────────────────────────────────────────
//   Root tree + parsing helpers
// ───────────────────────────────────────────────────────────────────────────────
const CheckoutTreeSchema_ = z.object({ rootNode: CheckoutTreeNodeSchema });
export const CheckoutTreeSchema: z.ZodType<CheckoutTree> = CheckoutTreeSchema_;
export interface CheckoutTree extends z.infer<typeof CheckoutTreeSchema_> {}

const CheckoutTreeOptionalIdsSchema_ = z.object({
  rootNode: CheckoutTreeNodeOptionalIdSchema,
});
const CheckoutTreeOptionalIdsSchema: z.ZodType<CheckoutTreeOptionalIds> =
  CheckoutTreeOptionalIdsSchema_;
export interface CheckoutTreeOptionalIds
  extends z.infer<typeof CheckoutTreeOptionalIdsSchema_> {}

export function parseCheckoutTree(checkoutTree: unknown): CheckoutTree {
  return CheckoutTreeSchema.parse(checkoutTree) as CheckoutTree;
}

export function safeParseCheckoutTree(
  checkoutTree: unknown,
): { success: true; value: CheckoutTree } | { success: false; error: Error } {
  const parsed = CheckoutTreeSchema.safeParse(checkoutTree);
  if (!parsed.success) {
    return { success: false, error: parsed.error };
  }
  return { success: true, value: parsed.data as CheckoutTree };
}

export function parseCheckoutTreeOptionalIds(
  checkoutTreeOptionalIds: unknown,
): CheckoutTreeOptionalIds {
  return CheckoutTreeOptionalIdsSchema.parse(
    checkoutTreeOptionalIds,
  ) as CheckoutTreeOptionalIds;
}

export function safeParseCheckoutTreeOptionalIds(
  checkoutTreeOptionalIds: unknown,
):
  | { success: true; value: CheckoutTreeOptionalIds }
  | { success: false; error: Error } {
  const parsed = CheckoutTreeOptionalIdsSchema.safeParse(
    checkoutTreeOptionalIds,
  );
  if (!parsed.success) {
    return { success: false, error: parsed.error };
  }
  return { success: true, value: parsed.data as CheckoutTreeOptionalIds };
}

/**
 * Normally, we would use `zodResponseFormat` to auto generate the json schema
 * but it doesn't handle recursive zod structures well.
 *
 * So we manually define the schema here.
 */
export const checkoutTreeJsonSchema = {
  type: "object",
  properties: { rootNode: { $ref: "#/$defs/CheckoutTreeNode" } },
  additionalProperties: false,
  required: ["rootNode"],
  $defs: {
    CheckoutTreeNode: {
      anyOf: [
        { $ref: "#/$defs/CheckoutTreeCheckoutExperienceNode" },
        { $ref: "#/$defs/CheckoutTreeSplitTestNode" },
        { $ref: "#/$defs/CheckoutTreeSegmentNode" },
      ],
    },
    CheckoutTreeCheckoutExperienceNode: {
      type: "object",
      properties: {
        nodeType: { enum: ["CHECKOUT_EXPERIENCE"] },
        checkoutExperienceId: { type: "string" },
      },
      additionalProperties: false,
      required: ["nodeType", "checkoutExperienceId"],
    },
    CheckoutTreeSplitTestNode: {
      type: "object",
      properties: {
        nodeType: { enum: ["SPLIT_TEST"] },
        splitTestId: { type: "string" },
        edges: {
          type: "array",
          items: { $ref: "#/$defs/CheckoutTreeSplitTestEdge" },
        },
      },
      additionalProperties: false,
      required: ["nodeType", "splitTestId", "edges"],
    },
    CheckoutTreeSplitTestEdge: {
      type: "object",
      properties: {
        treatmentId: { type: "string" },
        childNode: {
          anyOf: [
            { $ref: "#/$defs/CheckoutTreeCheckoutExperienceNode" },
            { $ref: "#/$defs/CheckoutTreeSegmentNode" },
          ],
        },
      },
      additionalProperties: false,
      required: ["childNode", "treatmentId"],
    },
    CheckoutTreeSegmentNode: {
      type: "object",
      properties: {
        nodeType: { enum: ["SEGMENT"] },
        matchingEdge: { $ref: "#/$defs/CheckoutTreeSegmentEdge" },
        nonMatchingEdge: { $ref: "#/$defs/CheckoutTreeSegmentEdge" },
        segment: { $ref: "#/$defs/Segment" },
      },
      additionalProperties: false,
      required: ["nodeType", "matchingEdge", "nonMatchingEdge", "segment"],
    },
    CheckoutTreeSegmentEdge: {
      type: "object",
      properties: { childNode: { $ref: "#/$defs/CheckoutTreeNode" } },
      additionalProperties: false,
      required: ["childNode"],
    },
    Segment: {
      anyOf: [
        { $ref: "#/$defs/OrderCountSegment" },
        { $ref: "#/$defs/DeliveryLocationSegment" },
        { $ref: "#/$defs/CustomerTagsSegment" },
        { $ref: "#/$defs/ProductTagsSegment" },
        { $ref: "#/$defs/ProductVendorSegment" },
        { $ref: "#/$defs/ItemQuantitySegment" },
        { $ref: "#/$defs/OriginLocationSegment" },
      ],
    },
    ItemQuantitySegment: {
      type: "object",
      properties: {
        segmentableCharacteristic: { enum: ["ITEM_QUANTITY"] },
        quantity: { type: "number" },
        operator: { enum: ["LESS_THAN", "LESS_THAN_OR_EQUAL", "EQUAL"] },
        exclusions: {
          type: "object",
          properties: { skus: { type: "array", items: { type: "string" } } },
          additionalProperties: false,
          required: ["skus"],
        },
      },
      additionalProperties: false,
      required: [
        "segmentableCharacteristic",
        "quantity",
        "operator",
        "exclusions",
      ],
    },
    OrderCountSegment: {
      type: "object",
      properties: {
        segmentableCharacteristic: { enum: ["ORDER_COUNT"] },
        numOrders: { type: "number" },
        operator: { enum: ["LESS_THAN", "LESS_THAN_OR_EQUAL", "EQUAL"] },
      },
      additionalProperties: false,
      required: ["segmentableCharacteristic", "numOrders", "operator"],
    },
    DeliveryLocationSegment_LocationCountry: {
      type: "object",
      properties: {
        countries: {
          type: "array",
          items: { type: "string", enum: COUNTRY_CODES },
        },
        operator: { enum: ["IN", "NOT_IN"] },
      },
      additionalProperties: false,
      required: ["countries", "operator"],
    },
    DeliveryLocationSegment_LocationProvince: {
      type: "object",
      properties: {
        provinces: {
          type: "array",
          items: { type: "string", enum: USA_PROVINCE_CODES },
        },
        operator: { enum: ["IN", "NOT_IN"] },
      },
      additionalProperties: false,
      required: ["provinces", "operator"],
    },
    DeliveryLocationSegment: {
      type: "object",
      properties: {
        segmentableCharacteristic: { enum: ["DELIVERY_LOCATION"] },
        locationCountry: {
          $ref: "#/$defs/DeliveryLocationSegment_LocationCountry",
        },
        locationProvince: {
          $ref: "#/$defs/DeliveryLocationSegment_LocationProvince",
        },
      },
      additionalProperties: false,
      required: [
        "segmentableCharacteristic",
        "locationCountry",
        "locationProvince",
      ],
    },
    CustomerTagsSegment: {
      type: "object",
      properties: {
        segmentableCharacteristic: { enum: ["CUSTOMER_TAGS"] },
        tags: { type: "array", items: { type: "string" } },
        operator: { enum: ["ANY_IN", "NONE_IN"] },
        customerTagsSegmentNodeIdentifier: {
          enum: [
            "CUSTOMER_TAGS_SEGMENT_NODE_ONE",
            "CUSTOMER_TAGS_SEGMENT_NODE_TWO",
          ],
        },
      },
      additionalProperties: false,
      required: [
        "segmentableCharacteristic",
        "tags",
        "operator",
        "customerTagsSegmentNodeIdentifier",
      ],
    },
    ProductTagsSegment: {
      type: "object",
      properties: {
        segmentableCharacteristic: { enum: ["PRODUCT_TAGS"] },
        tags: { type: "array", items: { type: "string" } },
        operator: { enum: ["ANY_IN", "NONE_IN"] },
        cartOperator: { enum: ["ALL_IN", "ANY_IN", "NONE_IN"] },
        productTagsSegmentNodeIdentifier: {
          enum: [
            "PRODUCT_TAGS_SEGMENT_NODE_ONE",
            "PRODUCT_TAGS_SEGMENT_NODE_TWO",
          ],
        },
      },
      additionalProperties: false,
      required: [
        "segmentableCharacteristic",
        "tags",
        "operator",
        "cartOperator",
        "productTagsSegmentNodeIdentifier",
      ],
    },
    ProductVendorSegment: {
      type: "object",
      properties: {
        segmentableCharacteristic: { enum: ["PRODUCT_VENDOR"] },
        vendors: { type: "array", items: { type: "string" } },
        operator: { enum: ["IN", "NOT_IN"] },
        cartOperator: { enum: ["ALL_IN", "ANY_IN", "NONE_IN"] },
      },
      additionalProperties: false,
      required: [
        "segmentableCharacteristic",
        "vendors",
        "operator",
        "cartOperator",
      ],
    },
    OriginLocationSegment: {
      type: "object",
      properties: {
        segmentableCharacteristic: { enum: ["ORIGIN_LOCATION"] },
        originAddress: {
          type: "object",
          properties: {
            address1: { type: "string" },
            address2: { type: "string" },
            city: { type: "string" },
            province: { type: "string" },
            postalCode: { type: "string" },
            country: { type: "string" },
          },
          additionalProperties: false,
          required: [
            "address1",
            "address2",
            "city",
            "province",
            "postalCode",
            "country",
          ],
        },
      },
      additionalProperties: false,
      required: ["segmentableCharacteristic", "originAddress"],
    },
  },
};

/**
 * A distillation of the information encoded in the checkout tree. Intended for
 * summarization of the checkout tree in the UI in places where it isn't
 * feasible to show the entire tree.
 */
export const CheckoutTreeOverviewSchema = z.object({
  segments: z.array(z.object({ label: z.string() })),
  checkoutExperiences: z.array(z.object({ id: z.string(), label: z.string() })),
});
export type CheckoutTreeOverview = z.infer<typeof CheckoutTreeOverviewSchema>;
